import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../../api/axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Toast from '../../ui/Toast';
import industryData from '../../data/industryData.json';
import countriesData from '../../data/countriesData.json';
import './MarketAnalysisView.css';

const MarketAnalysisView = ({ userId, refreshProfile, initialAnalysisId, onReset, onError }) => {
  const [query, setQuery] = useState('');
  const location = useLocation();
  const [isAnalyzing, setIsAnalyzing] = useState(
    !!initialAnalysisId || !!location.state?.initialAnalysisId
  );
  const [analysisId, setAnalysisId] = useState(
    initialAnalysisId || location.state?.initialAnalysisId || null
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [placeholderText, setPlaceholderText] = useState('');
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);
  const [analysisFailed, setAnalysisFailed] = useState(false);

  // Advanced Scope States
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [region, setRegion] = useState('');
  const [industry, setIndustry] = useState('');
  const [timeframe, setTimeframe] = useState('last_12_months');
  const [outputTone, setOutputTone] = useState('detailed');
  const [excludeKeywords, setExcludeKeywords] = useState('');
  const [specificFocus, setSpecificFocus] = useState('');
  const [includedSections, setIncludedSections] = useState(['Future Outlook']);

  const [expandedSections, setExpandedSections] = useState({});

  const toggleSectionExpansion = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const copyToClipboard = (text, sectionName) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could show toast here but let's keep it simple
      alert(`${sectionName} copied to clipboard!`);
    });
  };

  const [isPdfPreparing, setIsPdfPreparing] = useState(false);

  const getTopicSlug = (topic) => {
    return (topic || 'Analysis')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const availableSections = [
    'Market Size & Potential',
    'Competitor Analysis',
    'Pricing Strategy',
    'Consumer Behavior',
    'Future Outlook',
    'SWOT Analysis',
    'Regulatory Landscape',
    'Technological Trends',
  ];

  const toggleSection = (section) => {
    if (includedSections.includes(section)) {
      setIncludedSections(includedSections.filter((s) => s !== section));
    } else {
      setIncludedSections([...includedSections, section]);
    }
  };

  // Handle initialAnalysisId from Unified Search OR Navigation State (History re-entry)
  useEffect(() => {
    const finalId = initialAnalysisId || location.state?.initialAnalysisId;

    if (finalId) {
      setAnalysisId(finalId);
      setIsAnalyzing(true);
      setCurrentStep(0);
      setError(null);
      setAnalysisResult(null);
    }
  }, [initialAnalysisId, location.state]);

  // Use abort controller to cancel in-flight requests (prevents React StrictMode double-renders)
  const abortControllerRef = useRef(null);

  const suggestions = [
    'Analyze the electric scooter market in Southeast Asia.',
    'Give a market overview of premium home fragrance products in Malaysia.',
    'Market insights for mushroom farming in Malaysia.',
    'Analyze the organic food delivery market in Malaysia.',
  ];

  const steps = [
    { name: 'Analyzing query...', icon: 'fa-search' },
    { name: 'Searching web...', icon: 'fa-globe' },
    { name: 'Generating pdf...', icon: 'fa-file-pdf' },
  ];

  // Poll for analysis completion
  useEffect(() => {
    let pollInterval;

    if (isAnalyzing && analysisId) {
      // Simulate progress steps
      const progressInterval = setInterval(() => {
        setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
      }, 20000); // Move to next step every 20 seconds

      // Poll for actual completion
      pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`/market-analysis/${analysisId}`);

          if (response.data.success) {
            const analysis = response.data.data;

            if (analysis.status === 'completed') {
              clearInterval(pollInterval);
              clearInterval(progressInterval);
              setCurrentStep(steps.length);
              setAnalysisResult(analysis.analysis_result);
              setIsAnalyzing(false);

              // Refresh profile to reflect any credit adjustments or final state
              if (typeof refreshProfile === 'function') {
                refreshProfile();
              }
            } else if (analysis.status === 'failed') {
              clearInterval(pollInterval);
              clearInterval(progressInterval);

              // Extract exact error from analysis_result if available
              const errorMsg =
                analysis.analysis_result?.error || 'Analysis failed. Please try again.';

              const isCreditError =
                typeof errorMsg === 'string' &&
                (errorMsg.toLowerCase().includes('insufficient credits') ||
                  errorMsg.toLowerCase().includes('credit limit reached') ||
                  errorMsg.toLowerCase().includes('upgrade your tier'));

              // If the user wants a toast instead of the error page
              if (typeof onError === 'function') {
                onError(errorMsg);
              } else {
                setError(errorMsg);
              }
              setIsAnalyzing(false);
              setAnalysisId(null);
              setAnalysisFailed(false);
              if (typeof onReset === 'function') onReset();
            }
          }
        } catch (error) {
          console.error('Error polling analysis:', error);
        }
      }, 5000); // Poll every 5 seconds

      return () => {
        clearInterval(pollInterval);
        clearInterval(progressInterval);
      };
    }
  }, [isAnalyzing, analysisId]);

  useEffect(() => {
    const handleTyping = () => {
      const fullText = suggestions[suggestionIndex];
      setPlaceholderText((prev) =>
        isDeleting ? fullText.substring(0, prev.length - 1) : fullText.substring(0, prev.length + 1)
      );

      setTypingSpeed(isDeleting ? 50 : 100);

      if (!isDeleting && placeholderText === fullText) {
        setTimeout(() => setIsDeleting(true), 2000);
      } else if (isDeleting && placeholderText === '') {
        setIsDeleting(false);
        setSuggestionIndex((prev) => (prev + 1) % suggestions.length);
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [placeholderText, isDeleting, suggestionIndex, typingSpeed]);

  const handleStartAnalysis = async () => {
    if (query.trim().length < 10) {
      setError('Query is too short. Please enter at least 10 characters for better results.');
      return;
    }

    try {
      setIsAnalyzing(true);
      setCurrentStep(0);
      setError(null);
      setAnalysisResult(null);
      setAnalysisFailed(false);

      // Clear cached results when starting new analysis
      localStorage.removeItem('marketAnalysisResult');
      localStorage.removeItem('marketAnalysisQuery');
      localStorage.removeItem('marketAnalysisId');

      const response = await axios.post('/market-analysis', {
        user_id: userId,
        query: query,
        region: region,
        industry: industry,
        timeframe: timeframe,
        output_tone: outputTone,
        exclude_keywords: excludeKeywords,
        specific_focus: specificFocus,
        included_sections: includedSections,
      });

      if (response.data.success) {
        setAnalysisId(response.data.data.id);
        // Refresh credit balance immediately as it was deducted for starting the process
        if (typeof refreshProfile === 'function') {
          refreshProfile();
        }
      }
    } catch (error) {
      console.error('Error starting market analysis:', error);
      const backendError =
        error.response?.data?.error ||
        'Failed to start market analysis. Please check if API keys are configured.';

      const isCreditError =
        error.response?.status === 403 ||
        (typeof backendError === 'string' &&
          (backendError.toLowerCase().includes('insufficient credits') ||
            backendError.toLowerCase().includes('credit limit reached') ||
            backendError.toLowerCase().includes('upgrade your tier')));

      if (isCreditError) {
        let msg =
          typeof backendError === 'string'
            ? backendError
            : 'Insufficient credits to start research.';
        if (error.response?.status === 403) {
          msg = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span>Insufficient credits to start research.</span>
              <a href="/billing" style={{ color: '#2563eb', fontWeight: 'bold' }}>
                Top up / Buy credits
              </a>
            </div>
          );
        }

        if (typeof onError === 'function') {
          onError(msg);
          if (typeof onReset === 'function') onReset();
        } else {
          setError(msg);
        }
      } else {
        setError(backendError);
      }
      setIsAnalyzing(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
  };

  const handleBackToDashboard = () => {
    setIsAnalyzing(false);
    setAnalysisId(null);
    setAnalysisResult(null);
    setQuery('');
    setCurrentStep(0);
    setError(null);
    // Don't clear localStorage here if we want persistence across sessions/refreshes
  };

  const handleDownloadPDF = async (type) => {
    if (isPdfPreparing) return;
    setIsPdfPreparing(true);

    try {
      const response = await axios.get(`/market-analysis/${analysisId}/download/${type}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const topic = analysisResult.summary?.market || query || 'Research';
      const date = new Date().toISOString().split('T')[0];
      const filename = `Market-Research_${getTopicSlug(topic)}_${date}.pdf`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError(
        'PDF is still being prepared or failed to download. You can try again in a few seconds.'
      );
    } finally {
      setIsPdfPreparing(false);
    }
  };

  const clearQuery = () => {
    setQuery('');
  };

  // Helper function to render text (no more in-text citations)
  const renderTextWithReferences = (text, sources) => {
    if (!text) return null;

    // Render markdown with proper formatting
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...props }) => (
            <h1
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginTop: '15px',
                marginBottom: '10px',
                color: '#333',
              }}
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              style={{
                fontSize: '18px',
                fontWeight: 'bold',
                marginTop: '12px',
                marginBottom: '8px',
                color: '#444',
              }}
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                marginTop: '10px',
                marginBottom: '6px',
                color: '#555',
              }}
              {...props}
            />
          ),
          p: ({ node, ...props }) => (
            <p style={{ lineHeight: '1.8', color: '#555', marginBottom: '10px' }} {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul
              style={{ marginLeft: '20px', lineHeight: '1.8', marginBottom: '10px' }}
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              style={{ marginLeft: '20px', lineHeight: '1.8', marginBottom: '10px' }}
              {...props}
            />
          ),
          li: ({ node, ...props }) => (
            <li style={{ marginBottom: '5px', color: '#555' }} {...props} />
          ),
          strong: ({ node, ...props }) => (
            <strong style={{ fontWeight: '600', color: '#333' }} {...props} />
          ),
          em: ({ node, ...props }) => (
            <em style={{ fontStyle: 'italic', color: '#555' }} {...props} />
          ),
          a: ({ node, ...props }) => (
            <a
              style={{ color: '#0066cc', textDecoration: 'underline' }}
              {...props}
              target="_blank"
              rel="noopener noreferrer"
            />
          ),
          table: ({ node, ...props }) => (
            <table
              style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '15px' }}
              {...props}
            />
          ),
          th: ({ node, ...props }) => (
            <th
              style={{
                border: '1px solid #ddd',
                padding: '8px',
                background: '#f2f2f2',
                fontWeight: 'bold',
                textAlign: 'left',
              }}
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}
              {...props}
            />
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  // Show results view
  if (analysisResult) {
    // Generate dynamic title
    const year = new Date().getFullYear();
    const endYear = year + 2;
    const market = analysisResult.summary?.market || 'Market';
    const geography = analysisResult.summary?.geography || 'Global';
    const dynamicTitle = `${geography.toUpperCase()} ${market.toUpperCase()} – MARKET INSIGHT REPORT (${year}−${endYear})`;

    return (
      <div
        className="market-analysis-view"
        style={{
          height: '100vh',
          overflow: 'auto',
          background: '#f8fafc',
          position: 'relative',
        }}
      >
        {/* Main Layout Container */}
        <div style={{ display: 'flex', maxWidth: '1440px', margin: '0 auto', padding: '0 40px' }}>
          {/* Report Content - Full Width */}
          <div
            className="results-container"
            style={{ flex: 1, margin: '0 auto', padding: '40px 0', maxWidth: '1000px' }}
          >
            {/* Header */}
            <div
              style={{
                marginBottom: '40px',
                paddingBottom: '30px',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <button
                onClick={handleBackToDashboard}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  cursor: 'pointer',
                  fontSize: '13px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  transition: 'color 0.2s',
                  fontWeight: '600',
                  padding: '0',
                }}
              >
                <i className="fas fa-arrow-left"></i> Back to search
              </button>
              <h1
                style={{
                  margin: '0 0 16px 0',
                  color: '#0f172a',
                  fontSize: '32px',
                  fontWeight: '800',
                  lineHeight: '1.2',
                }}
              >
                {dynamicTitle}
              </h1>
            </div>

            {/* Action Bar */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '40px',
                maxWidth: '1000px',
              }}
            >
              <button
                onClick={() => handleDownloadPDF('full')}
                className={`btn-download-full ${isPdfPreparing ? 'preparing' : ''}`}
                disabled={isPdfPreparing}
              >
                <i className={isPdfPreparing ? 'fas fa-sync fa-spin' : 'fas fa-file-pdf'}></i>
                {isPdfPreparing
                  ? 'Preparing PDF and synchronizing data...'
                  : 'Download Full Research PDF'}
              </button>
              {isPdfPreparing && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingLeft: '4px',
                  }}
                >
                  <div className="mini-progress-bar"></div>
                  <span>Generating high-resolution report... usually takes 10-15s</span>
                </div>
              )}
            </div>

            {/* Stats Overview */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '20px',
                marginBottom: '60px',
                maxWidth: '1000px',
              }}
            >
              <div className="stat-card">
                <div className="stat-label">
                  <i className="fas fa-globe"></i>Sources Analyzed
                </div>
                <div className="stat-value">
                  {analysisResult.summary?.totalSourcesAnalyzed || 0}
                </div>
                <div className="stat-subtext">Verified web data points</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">
                  <i className="fas fa-clock"></i>Processing Time
                </div>
                <div className="stat-value">
                  {analysisResult.summary?.processingTime || 'Under 2m'}
                </div>
                <div className="stat-subtext">Deep market research</div>
              </div>
            </div>

            {/* Report Sections */}
            <div
              style={{
                maxWidth: '1000px',
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
                marginBottom: '80px',
              }}
            >
              {/* Executive Summary */}
              {analysisResult.report?.executiveSummary && (
                <div id="exec-summary" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-file-alt"></i> Executive Summary
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(analysisResult.report.executiveSummary, 'Executive Summary')
                      }
                      title="Copy Summary"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.executiveSummary)}
                  </div>
                </div>
              )}

              {/* Market Overview */}
              {analysisResult.report?.marketOverview && (
                <div id="market-overview" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-chart-pie"></i> Market Overview
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(analysisResult.report.marketOverview, 'Market Overview')
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.marketOverview)}
                  </div>
                </div>
              )}

              {/* Market Size & Potential */}
              {analysisResult.report?.marketSizeAndPotential && (
                <div id="market-size" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-expand-arrows-alt"></i> Market Size & Potential
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(analysisResult.report.marketSizeAndPotential, 'Market Size')
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.marketSizeAndPotential)}
                  </div>
                </div>
              )}

              {/* Highlighted Key Statistics Block */}
              {(analysisResult.report?.keyStatistics || analysisResult.summary?.keyPlayers) && (
                <div id="key-stats" className="report-card-ma key-stats-highlight pulsate-glow">
                  <h3 className="report-section-title" style={{ color: '#1e3a8a' }}>
                    <i className="fas fa-hashtag" style={{ color: '#1e3a8a' }}></i> Key Market
                    Statistics
                  </h3>
                  <div className="stats-grid-highlight">
                    {/* If stats are in report, render them, else fallback to something or show mock placeholders if needed */}
                    {analysisResult.report?.keyStatistics ? (
                      <div className="report-section-content">
                        {renderTextWithReferences(analysisResult.report.keyStatistics)}
                      </div>
                    ) : (
                      <div className="no-stats-placeholder">
                        Detailed statistics are available in the full report below.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Key Trends */}
              {analysisResult.report?.keyTrends && (
                <div id="key-trends" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-bolt"></i> Key Trends
                    </h3>
                    <div className="card-actions">
                      <button
                        className="copy-btn-ma"
                        onClick={() =>
                          copyToClipboard(analysisResult.report.keyTrends, 'Key Trends')
                        }
                        title="Copy Section"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                  </div>
                  <div
                    className={`report-section-content ${expandedSections['trends'] ? 'expanded' : 'collapsible-content'}`}
                  >
                    {renderTextWithReferences(analysisResult.report.keyTrends)}
                  </div>
                  {analysisResult.report.keyTrends.length > 500 && (
                    <button
                      className="expand-toggle-ma"
                      onClick={() => toggleSectionExpansion('trends')}
                    >
                      {expandedSections['trends'] ? 'Show Less' : 'Read Full Trends'}
                    </button>
                  )}
                </div>
              )}

              {/* Pricing Strategy */}
              {analysisResult.report?.pricingStrategy && (
                <div id="pricing" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-tag"></i> Pricing Strategy
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(analysisResult.report.pricingStrategy, 'Pricing Strategy')
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.pricingStrategy)}
                  </div>
                </div>
              )}

              {/* Consumer Behavior */}
              {analysisResult.report?.consumerBehavior && (
                <div id="consumer" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-brain"></i> Consumer Behavior
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(analysisResult.report.consumerBehavior, 'Consumer Behavior')
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.consumerBehavior)}
                  </div>
                </div>
              )}

              {/* Opportunities */}
              {analysisResult.report?.opportunities && (
                <div id="opportunities" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-lightbulb"></i> Opportunities
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(analysisResult.report.opportunities, 'Opportunities')
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.opportunities)}
                  </div>
                </div>
              )}

              {/* Risks & Challenges */}
              {analysisResult.report?.risksAndChallenges && (
                <div id="risks" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-shield-alt"></i> Risks & Challenges
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(
                          analysisResult.report.risksAndChallenges,
                          'Risks & Challenges'
                        )
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.risksAndChallenges)}
                  </div>
                </div>
              )}

              {/* Competitive Landscape */}
              {analysisResult.report?.competitiveLandscape && (
                <div id="competitors" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-users"></i> Competitive Landscape
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(
                          analysisResult.report.competitiveLandscape,
                          'Competitive Landscape'
                        )
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.competitiveLandscape)}
                  </div>
                </div>
              )}

              {/* Future Outlook */}
              {analysisResult.report?.futureOutlook && (
                <div id="future" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-forward"></i> Future Outlook
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(analysisResult.report.futureOutlook, 'Future Outlook')
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.futureOutlook)}
                  </div>
                </div>
              )}

              {/* SWOT Analysis */}
              {analysisResult.report?.swotAnalysis && (
                <div id="swot" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-balance-scale"></i> SWOT Analysis
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(analysisResult.report.swotAnalysis, 'SWOT Analysis')
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.swotAnalysis)}
                  </div>
                </div>
              )}

              {/* Regulatory Landscape */}
              {analysisResult.report?.regulatoryLandscape && (
                <div id="regulatory" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-gavel"></i> Regulatory Landscape
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(
                          analysisResult.report.regulatoryLandscape,
                          'Regulatory Landscape'
                        )
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.regulatoryLandscape)}
                  </div>
                </div>
              )}

              {/* Technological Trends */}
              {analysisResult.report?.technologicalTrends && (
                <div id="tech-trends" className="report-card-ma report-card-interactive">
                  <div className="report-card-header">
                    <h3 className="report-section-title">
                      <i className="fas fa-microchip"></i> Technological Trends
                    </h3>
                    <button
                      className="copy-btn-ma"
                      onClick={() =>
                        copyToClipboard(
                          analysisResult.report.technologicalTrends,
                          'Technological Trends'
                        )
                      }
                      title="Copy Section"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                  </div>
                  <div className="report-section-content">
                    {renderTextWithReferences(analysisResult.report.technologicalTrends)}
                  </div>
                </div>
              )}

              {/* Sources Section - Left-aligned within the container */}
              {analysisResult.report?.sources && analysisResult.report.sources.length > 0 && (
                <div id="sources" className="sources-container-refined">
                  <h3 className="sources-title">
                    <i className="fas fa-link"></i>
                    References
                  </h3>
                  <div className="sources-list-refined">
                    {analysisResult.report.sources.map((source, index) => {
                      const domain = new URL(source.url).hostname.replace('www.', '');
                      const author = source.source || domain;
                      const year = source.date
                        ? source.date.match(/\d{4}/)
                          ? source.date.match(/\d{4}/)[0]
                          : 'n.d.'
                        : 'n.d.';

                      return (
                        <div
                          key={index}
                          className="source-item-apa"
                          style={{
                            marginBottom: '16px',
                            padding: '12px 16px',
                            background: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontSize: '14px',
                            color: '#475569',
                            lineHeight: '1.6',
                          }}
                        >
                          <span
                            style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '8px' }}
                          >
                            [{source.id}]
                          </span>
                          <span style={{ fontWeight: '600', color: '#1e293b' }}>{author}</span> (
                          {year}).
                          <i style={{ marginLeft: '4px' }}>{source.title}</i>. Retrieved from{' '}
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#2563eb',
                              textDecoration: 'underline',
                              marginLeft: '4px',
                              wordBreak: 'break-all',
                            }}
                          >
                            {source.url}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show processing view
  if (isAnalyzing) {
    return (
      <div className="market-analysis-view">
        <div
          className="progress-container"
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            maxWidth: '600px',
            margin: '0 auto',
          }}
        >
          <div className="logo" style={{ marginBottom: '30px' }}>
            <img src="/image/Logo.png" alt="Company Logo" style={{ maxWidth: '200px' }} />
          </div>

          <div className="market-analysis-loader">
            <div className="radar-ring"></div>
            <div className="radar-core"></div>
            <div className="scanning-line"></div>
            <div className="data-particles"></div>
          </div>

          <div
            style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '10px' }}
          >
            Deep Market Analysis in Progress...
          </div>

          <div style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            This typically takes 1-2 minutes
          </div>

          <div
            style={{
              fontSize: '13px',
              color: '#2563eb',
              background: '#eff6ff',
              padding: '10px 20px',
              borderRadius: '20px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '40px',
            }}
          >
            <i className="fas fa-info-circle"></i>
            You can safely leave this page. Your analysis will continue in the background. Access it
            later in <strong>Analysis Reports</strong>.
          </div>

          {error && <Toast message={error} onClose={() => setError(null)} />}

          {/* Progress Steps */}
          <div
            style={{
              textAlign: 'left',
              background: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '25px',
              marginBottom: '30px',
            }}
          >
            {steps.map((step, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '15px',
                  opacity: index <= currentStep ? 1 : 0.3,
                  transition: 'opacity 0.3s',
                }}
              >
                <div
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background:
                      index < currentStep
                        ? '#2563eb'
                        : index === currentStep
                          ? '#2563eb'
                          : '#e0e0e0',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '15px',
                    fontSize: '14px',
                  }}
                >
                  {index < currentStep ? (
                    <i className="fas fa-check"></i>
                  ) : index === currentStep ? (
                    <i className={`fas fa-spinner fa-spin`}></i>
                  ) : (
                    <i className={`fas ${step.icon}`}></i>
                  )}
                </div>
                <div style={{ fontSize: '14px', color: '#333' }}>{step.name}</div>
              </div>
            ))}
          </div>

          <button
            onClick={handleBackToDashboard}
            style={{
              padding: '12px 30px',
              background: 'white',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // Default: show nothing if not analyzing and no result
  return null;
};

export default MarketAnalysisView;
